import { useRef, useState } from 'react'

export const useDeferredQueueValue = <GValue>(
  initial: GValue,
  duration = 5_000,
) => {
  const [value, setValue] = useState<GValue | null>(initial)
  const queue = useRef<GValue[]>([])
  const timer = useRef<NodeJS.Timeout | null>(null)
  const lastSetAt = useRef<number>(Date.now())

  const processQueue = () => {
    const now = Date.now()
    const next = queue.current.shift() ?? null
    setValue(next)
    lastSetAt.current = now
    if (queue.current.length > 0) {
      timer.current = setTimeout(processQueue, duration)
    } else {
      timer.current = null
    }
  }

  const enqueue = (newValue: GValue, nullable?: boolean) => {
    const now = Date.now()
    if (value === null || now - lastSetAt.current >= duration) {
      setValue(newValue)
      lastSetAt.current = now
    } else {
      queue.current.push(newValue)
    }
    if (!timer.current && nullable !== false) {
      timer.current = setTimeout(
        processQueue,
        duration - (now - lastSetAt.current),
      )
    }
  }

  const setImmediately = (newValue: GValue, nullable?: boolean) => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    queue.current = []
    setValue(newValue)
    lastSetAt.current = Date.now()
    if (nullable !== false) {
      timer.current = setTimeout(processQueue, duration)
    }
  }

  return { value, enqueue, setImmediately }
}
